import { FC, useEffect, useState } from 'react';
import { Accordion, AccordionBody, AccordionHeader, AccordionItem, Button } from 'reactstrap';
import { enqueueSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { GroupedObject, ObjectItem } from 'api/clients/types';
import './ClientsAcordion.scss';
import CustomDropdown from 'components/Dropdown/Dropdown';
import ClientTable from 'components/ClientTable/ClientTable';
import ManageClientModal from 'pages/ClientDashboard/Modals/ManageClientModal';
// @ts-ignore
import clientLogo from 'assets/images/clientLogo.png';
import DeleteModal from 'components/DeleteModal/DeleteModal';
import useDeleteClient from 'hooks/Clients/useDeleteClient';
import { useAddImageToClientStore } from 'pages/ClientDashboard/Modals/AddImageToClient/useAddImageToClientStore';
import { AddImageToClientModal } from 'pages/ClientDashboard/Modals/AddImageToClient/AddImageToClientModal';
import UpdateClientModal from 'pages/ClientDashboard/Modals/UpdateClientModal';
import ManageObjectModal from 'components/ClientTable/Modals/ManageObjectModal';
import { useManageEmailNotifyModalStore } from 'components/ClientTable/Modals/NotifyModal/ManageEmailNotifyModalStore';

interface ClientsAccordionProps {
  data: GroupedObject[];
  refetchClients: () => void;
}
const ClientsAccordion: FC<ClientsAccordionProps> = ({ data, refetchClients }) => {
  const [open, setOpen] = useState('0');
  const { onAddImageToClientOpen } = useAddImageToClientStore();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isMangeModalOpen, setIsManageModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isManageObjectModalOpen, setIsManageObjectModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<GroupedObject | undefined>(undefined);
  const { deleteClient } = useDeleteClient();
  const { onManageEmailNotifyModalOpen } = useManageEmailNotifyModalStore();
  const [selectedObject, setSelectedObject] = useState<ObjectItem | undefined>(undefined);
  const [searchParams, setSearchParams] = useSearchParams();
  const companyParam = searchParams.get('company');
  const slugParam = searchParams.get('slug');
  const handleCloseModal = () => {
    const currentUrl = new URL(window.location.href);

    currentUrl.searchParams.delete('company');
    currentUrl.searchParams.delete('slug');
    currentUrl.searchParams.delete('type');
    window.history.replaceState({}, '', currentUrl.toString());
  };
  useEffect(() => {
    if (companyParam) {
      setOpen(companyParam);
    }

    if (slugParam && data.length > 0) {
      const targetSite = data.flatMap(client => client.object_list).find(site => site.slug === slugParam);

      if (targetSite) {
        const channels = [
          { key: 'mct', available: targetSite.mct_count > 0 },
          { key: 'srbm', available: targetSite.srbm_count > 0 },
          { key: 'smst', available: targetSite.smst_count > 0 },
          { key: 'srst', available: targetSite.srst_count > 0 },
        ];
        const availableChannelsKeys = channels.filter(c => c.available).map(c => c.key);

        onManageEmailNotifyModalOpen(
          targetSite.slug,
          targetSite.email_messagess_send.mct_receive_messages || false,
          targetSite.email_messagess_send.srbm_receive_messages || false,
          targetSite.email_messagess_send.smst_receive_messages || false,
          targetSite.email_messagess_send.srst_receive_messages || false,
          targetSite.mct_count,
          targetSite.srbm_count,
          targetSite.smst_count,
          targetSite.srst_count,
          availableChannelsKeys,
        );

        handleCloseModal();
      }
    }
  }, [companyParam, slugParam, data, onManageEmailNotifyModalOpen, searchParams, setSearchParams]);
  const { t } = useTranslation();
  const toggle = (id: string) => {
    if (open === id) {
      setOpen('0');
    } else {
      setOpen(id);
      setSelectedClient(data.find(item => item.id === +id));
    }
  };

  const onDeleteClient = () => {
    deleteClient(selectedClient?.id || 0, {
      onError: (error: any) => {
        if (error.response.data.detail) {
          enqueueSnackbar(error.response.data.detail, { variant: 'error' });
        }
      },
      onSuccess: () => {
        enqueueSnackbar(t('ClientsAccordion.accordion.success-message'), { variant: 'success' });
        setIsDeleteModalOpen(false);
        setSelectedClient(undefined);
      },
    });
  };

  return (
    <>
      <Accordion open={open} toggle={toggle} className="d-flex flex-column gap-1 p-3 ">
        {data.map(item => (
          <AccordionItem key={item.id} className="shadow-lg">
            <AccordionHeader targetId={`${item.id}`}>
              <h2 className="accordion-header ml_display w-100">
                <div className="team-list list-view-filter">
                  <div className="card m-0 team-box hover-effect">
                    <div className="card-body py-1 collapsed">
                      <div className="row align-items-center team-row justify-content-center justify-content-xl-between">
                        <div className="col-xl-3 col-lg-8">
                          <div className="team-profile-img d-flex">
                            {item.image ? (
                              <div
                                onClick={e => {
                                  e.stopPropagation();
                                  onAddImageToClientOpen(item.id);
                                  setIsAddModalOpen(false);
                                }}
                              >
                                <img
                                  src={item.image ? item.image : clientLogo}
                                  alt="avatar"
                                  title={
                                    item.image
                                      ? t('ClientsAccordion.accordion.accordion-options.help-text-img-change')
                                      : t('ClientsAccordion.accordion.accordion-options.help-text-img')
                                  }
                                  className="rounded-1 avatar-md object-fit-contain"
                                  style={{ minWidth: '60px', maxHeight: '70px' }}
                                />
                              </div>
                            ) : (
                              <div
                                title={t('ClientsAccordion.accordion.accordion-options.help-text-img')}
                                className="rounded-1 avatar-md object-fit-contain d-flex align-items-center justify-content-center"
                                style={{
                                  minWidth: '60px',
                                  maxHeight: '70px',
                                  border: '1px dashed #6f42c1',
                                  background: '#f3eaff',
                                  color: '#6f42c1',
                                  fontSize: 32,
                                  fontWeight: 'bold',
                                  padding: 0,
                                  transition: 'background 0.2s, box-shadow 0.2s, border-color 0.2s',
                                }}
                                onClick={e => {
                                  e.stopPropagation();
                                  onAddImageToClientOpen(item.id);
                                  setIsAddModalOpen(true);
                                }}
                                role="button"
                                onMouseOver={e => {
                                  e.currentTarget.style.background = '#d4b0f5';
                                  e.currentTarget.style.borderColor = '#5f00cc';
                                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(111,66,193,0.15)';
                                }}
                                onMouseOut={e => {
                                  e.currentTarget.style.background = '#f3eaff';
                                  e.currentTarget.style.borderColor = '#6f42c1';
                                  e.currentTarget.style.boxShadow = 'none';
                                }}
                              >
                                +
                              </div>
                            )}

                            <div className="team-content text-body-emphasis" title={item.name}>
                              <span className="text-body d-block fs-18">{item.name}</span>
                              <p className="text-muted mb-0 fs-14 ">
                                <em>{t('ClientsAccordion.accordion.accordion-item.company')}</em>
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="col-xl-1 col-lg-2 px-0" title={item.city}>
                          <h6 className="mb-0 mt-2 fs-18 text-center text-wrap">{item.city}</h6>
                          <p className="text-muted text-center mb-1 fs-14">
                            <em>{t('ClientsAccordion.accordion.accordion-item.city')}</em>
                          </p>
                        </div>
                        <div className="col-xl-1 col-lg-6" title={`${item.object_count}`}>
                          <h6 className="mb-0 mt-2 fs-18 text-center text-wrap">{item.object_count}</h6>
                          <p className="text-muted mb-1 fs-14 text-center">
                            <em>{t('ClientsAccordion.accordion.accordion-item.object-count')}</em>
                          </p>
                        </div>
                        <div className="col-xl-2 col-lg-6" title={`${item.owner?.last_name} ${item.owner?.first_name}`}>
                          <h6 className="mb-0 mt-2 fs-18 text-center text-wrap">
                            {item.owner?.last_name} {item.owner?.first_name}
                          </h6>
                          <p className="text-muted mb-1 fs-14 text-center">
                            <em>{t('ClientsAccordion.accordion.accordion-item.team-leader')}</em>
                          </p>
                        </div>
                        <div
                          className="col-xl-2 col-lg-2 d-flex gap-2 justify-content-center align-items-center"
                          title={`${item.mct_count + item.srbm_count + item.smst_count + item.srst_count} - ${t('ClientsAccordion.accordion.accordion-item.device-count')}`}
                        >
                          <div style={{ width: 40 }}>
                            <h6 className="mb-0 mt-2 fs-18 text-center">{item.mct_count}</h6>
                            <p className="text-muted mb-1 fs-14 text-center d-md-none d-lg-block">
                              <em title={t('ClientsAccordion.accordion.accordion-item.MCT')}>
                                {t('ClientsAccordion.accordion.accordion-item.mct-count')}
                              </em>
                            </p>
                          </div>
                          <div style={{ width: 40 }}>
                            <h6 className="mb-0 mt-2 fs-18 text-center">{item.srbm_count}</h6>
                            <p className="text-muted mb-1 fs-14 text-center d-md-none d-lg-block">
                              <em title={t('ClientsAccordion.accordion.accordion-item.SRBM')}>
                                {t('ClientsAccordion.accordion.accordion-item.srbm-count')}
                              </em>
                            </p>
                          </div>
                          <div style={{ width: 40 }}>
                            <h6 className="mb-0 mt-2 fs-18 text-center">{item.smst_count}</h6>
                            <p className="text-muted mb-1 fs-14 text-center d-md-none d-lg-block">
                              <em title={t('ClientsAccordion.accordion.accordion-item.SMST')}>
                                {t('ClientsAccordion.accordion.accordion-item.smst-count')}
                              </em>
                            </p>
                          </div>
                          <div style={{ width: 40 }}>
                            <h6 className="mb-0 mt-2 fs-18 text-center">{item.srst_count}</h6>
                            <p className="text-muted mb-1 fs-14 text-center d-md-none d-lg-block">
                              <em title={t('ClientsAccordion.accordion.accordion-item.SRST')}>
                                {t('ClientsAccordion.accordion.accordion-item.srst-count')}
                              </em>
                            </p>
                          </div>
                        </div>
                        <div className="col-xl-1 col-lg-6 justify-content-center d-flex">
                          <Button
                            onClick={e => {
                              e.currentTarget.blur();
                              e.stopPropagation();
                              setSelectedObject(undefined);
                              setSelectedClient(item);
                              setIsManageObjectModalOpen(true);
                            }}
                            className="btn text-center btn-ghost-success m-1"
                            data-key="t-add"
                          >
                            + {t('ClientTable.add-object')}
                          </Button>
                        </div>
                        <div className="col-xl-1 col-lg-12 d-flex justify-content-center">
                          <CustomDropdown
                            button={<i className="bx bx-dots-horizontal fs-16" />}
                            options={[
                              {
                                name: t('ClientsAccordion.accordion.accordion-options.edit'),
                                icon: 'ri-pencil-fill ',
                                onClick: () => {
                                  setSelectedClient(item);
                                  setIsUpdateModalOpen(true);
                                },
                              },
                              {
                                name: t('ClientsAccordion.accordion.accordion-options.delete'),
                                icon: 'ri-delete-bin-fill ',
                                onClick: () => {
                                  setSelectedClient(item);
                                  setIsDeleteModalOpen(true);
                                },
                              },
                            ]}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </h2>
            </AccordionHeader>
            <AccordionBody accordionId={`${item.id}`}>
              <ClientTable
                data={item.object_list}
                rootClient={item}
                selectedObject={selectedObject}
                setSelectedObject={setSelectedObject}
                setIsManageObjectModalOpen={setIsManageObjectModalOpen}
                refetchClients={refetchClients}
              />
            </AccordionBody>
          </AccordionItem>
        ))}
      </Accordion>
      <AddImageToClientModal refetch={refetchClients} isAddModal={isAddModalOpen} />
      <ManageClientModal
        clientId={selectedClient?.id}
        isOpen={isMangeModalOpen}
        onClose={() => {
          setSelectedClient(undefined);
          setIsManageModalOpen(false);
        }}
        refetchClients={refetchClients}
      />
      <ManageObjectModal
        rootClient={selectedClient}
        objectId={selectedObject?.id}
        isOpen={isManageObjectModalOpen}
        onClose={() => {
          setSelectedObject(undefined);
          setIsManageObjectModalOpen(false);
        }}
        refetchClients={refetchClients}
      />
      <UpdateClientModal
        clientId={selectedClient?.id}
        isOpen={isUpdateModalOpen}
        onClose={() => {
          setSelectedClient(undefined);
          setIsUpdateModalOpen(false);
        }}
        refetchClients={refetchClients}
      />
      <DeleteModal
        title={t('ClientsAccordion.accordion.delete-modal-title')}
        objectName={selectedClient?.name || ''}
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onDelete={onDeleteClient}
      />
    </>
  );
};

export default ClientsAccordion;
